import React, { useState, useEffect } from 'react';
import { homeworkService } from '../services/api';
import Navigation from '../components/Navigation';
import LoadingSpinner from '../components/LoadingSpinner';
import SOSButton from '../components/SOSButton';
import { Badge, Button, Card } from '../components/ui';
import { Download } from 'lucide-react';

const HOMEWORK_RESOURCES = {
  mindfulness: {
    title: 'Mindfulness & Awareness',
    description: 'Foundational practices for managing anxiety, stress, and building emotional awareness',
    icon: '🧘',
    color: 'bg-blue-50 border-blue-200',
    topics: ['Anxiety Management', 'Stress Reduction', 'Emotional Regulation', 'Present Moment Awareness'],
    recommendedFor: 'Early mental wellness journey, building foundational skills',
  },
  advanced: {
    title: 'Advanced Mental Wellness Companion Program',
    description: 'Comprehensive system for CBT skills, mindfulness practices, and long-term behavior change',
    icon: '🎯',
    color: 'bg-purple-50 border-purple-200',
    topics: ['Trauma Healing', 'Wellness Maintenance', 'Identity Reconstruction', 'Behavior Change', 'Coping Strategies'],
    recommendedFor: 'Mid to advanced mental wellness journey, deeper therapeutic work',
  },
  insurance: {
    title: 'Clinical Documentation Resources',
    description: 'Structured templates with medical necessity language for clinical documentation',
    icon: '📋',
    color: 'bg-green-50 border-green-200',
    topics: ['Documentation', 'Treatment Planning', 'Progress Tracking', 'Compliance'],
    recommendedFor: 'Working with clinicians on insurance-covered treatment',
  }
};

const HomeworkResourcesPage = () => {
  const [loading, setLoading] = useState(true);
  const [assignedHomework, setAssignedHomework] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [downloadingIds, setDownloadingIds] = useState(new Set());
  const [downloadError, setDownloadError] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [assignedResponse, templatesResponse] = await Promise.all([
        homeworkService.getAssignedHomework().catch(() => ({ data: [] })),
        homeworkService.getTemplates().catch(() => ({ data: [] }))
      ]);

      const assigned = Array.isArray(assignedResponse.data)
        ? assignedResponse.data
        : assignedResponse.data?.data || [];
      const availableTemplates = Array.isArray(templatesResponse.data)
        ? templatesResponse.data
        : templatesResponse.data?.data || [];

      setAssignedHomework(assigned);
      setTemplates(availableTemplates);
    } catch (error) {
      console.error('Failed to load homework resources:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category === selectedCategory ? null : category);
  };

  const getTemplatesForCategory = (category) => {
    return templates.filter(t => t.setId === category);
  };

  const handleDownload = async (template) => {
    setDownloadingIds(prev => new Set([...prev, template.id]));
    setDownloadError(null);
    try {
      // For public template downloads, we can use window.open on the pdfUrl directly
      // but we'll enhance this to handle authentication properly
      const fileName = `${(template.title || 'homework').replace(/[^a-z0-9-_ ]/gi, '').trim() || 'homework'}.pdf`;
      
      // Use fetch with credentials to maintain session
      const response = await fetch(template.pdfUrl, {
        credentials: 'include', // Include cookies for authentication
        headers: {
          'Accept': 'application/pdf'
        }
      });

      if (!response.ok) {
        throw new Error(`Download failed with status ${response.status}`);
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Download failed:', error);
      setDownloadError(`Failed to download ${template.title}. Please try again.`);
      setTimeout(() => setDownloadError(null), 5000);
    } finally {
      setDownloadingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(template.id);
        return newSet;
      });
    }
  };

  const handleAssignedHomeworkDownload = async (assignment) => {
    setDownloadingIds(prev => new Set([...prev, assignment._id]));
    setDownloadError(null);
    try {
      const fileName = `${(assignment.title || 'homework').replace(/[^a-z0-9-_ ]/gi, '').trim() || 'homework'}.pdf`;
      
      const response = await fetch(assignment.pdfUrl, {
        credentials: 'include',
        headers: {
          'Accept': 'application/pdf'
        }
      });

      if (!response.ok) {
        throw new Error(`Download failed with status ${response.status}`);
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Download failed:', error);
      setDownloadError(`Failed to download ${assignment.title}. Please try again.`);
      setTimeout(() => setDownloadError(null), 5000);
    } finally {
      setDownloadingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(assignment._id);
        return newSet;
      });
    }
  };

  if (loading) {
    return (
      <>
        <Navigation />
        <div className="flex items-center justify-center min-h-screen">
          <LoadingSpinner />
        </div>
      </>
    );
  }

  const categoryTemplates = selectedCategory ? getTemplatesForCategory(selectedCategory) : [];
  const sortedTemplates = [...categoryTemplates].sort((a, b) => {
    const typeA = a.type || `${a.setId} - ${a.title}`;
    const typeB = b.type || `${b.setId} - ${b.title}`;
    return typeA.localeCompare(typeB);
  });

  return (
    <>
      <Navigation />
      <main className="max-w-7xl mx-auto px-6 py-10">
        {/* Header */}
        <div className="mb-8">
          <Badge variant="primary" size="sm">Homework Resources</Badge>
          <h1 className="mt-3 text-4xl font-display font-bold text-neutral-900">
            Therapeutic Homework Library
          </h1>
          <p className="mt-3 text-lg text-neutral-600">
            Access evidence-based worksheets and exercises to support your mental wellness journey. 
            These resources are organized by worksheet type and topic so you can quickly find what fits your current goals.
          </p>
        </div>

        {/* Assigned Homework Section */}
        {assignedHomework.length > 0 && (
          <Card className="mb-8 bg-gradient-to-r from-primary-50 to-blue-50">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 p-3 bg-primary-500 rounded-lg text-white">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-bold text-neutral-900 mb-2">
                  Homework Assigned by Your Clinician
                </h2>
                <p className="text-neutral-600 mb-4">
                  You have {assignedHomework.length} active assignment(s) from your clinician
                </p>
                <div className="space-y-2">
                  {assignedHomework.slice(0, 3).map((assignment) => (
                    <div
                      key={assignment._id}
                      className="flex items-center justify-between p-3 bg-white rounded-lg border border-neutral-200"
                    >
                      <div>
                        <div className="font-medium text-neutral-900">{assignment.title}</div>
                        {assignment.dueDate && (
                          <div className="text-sm text-neutral-500">
                            Due: {new Date(assignment.dueDate).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                      <Button
                        size="sm"
                        onClick={() => handleAssignedHomeworkDownload(assignment)}
                        disabled={downloadingIds.has(assignment._id)}
                      >
                        <Download className="h-3 w-3" />
                        {downloadingIds.has(assignment._id) ? 'Downloading...' : 'Download'}
                      </Button>
                    </div>
                  ))}
                </div>
                {assignedHomework.length > 3 && (
                  <button
                    onClick={() => {/* Navigate to full homework page */}}
                    className="mt-3 text-sm font-medium text-primary-600 hover:text-primary-700"
                  >
                    View all {assignedHomework.length} assignments →
                  </button>
                )}
              </div>
            </div>
          </Card>
        )}

        {/* Resource Categories */}
        <div className="grid gap-6 md:grid-cols-3 mb-8">
          {Object.entries(HOMEWORK_RESOURCES).map(([key, category]) => {
            const isSelected = selectedCategory === key;
            return (
              <Card
                key={key}
                className={`cursor-pointer transition-all hover:shadow-lg ${
                  isSelected ? 'ring-2 ring-primary-500' : ''
                }`}
                onClick={() => handleCategorySelect(key)}
              >
                <div className={`p-4 rounded-lg ${category.color} mb-4`}>
                  <div className="text-4xl mb-2">{category.icon}</div>
                  <h3 className="text-lg font-bold text-neutral-900">{category.title}</h3>
                </div>
                <p className="text-sm text-neutral-600 mb-4">{category.description}</p>
                
                <div className="mb-4">
                  <div className="text-xs font-semibold text-neutral-500 uppercase mb-2">Topics Covered</div>
                  <div className="flex flex-wrap gap-1">
                    {category.topics.slice(0, 3).map((topic, idx) => (
                      <Badge key={idx} variant="neutral" size="sm">{topic}</Badge>
                    ))}
                    {category.topics.length > 3 && (
                      <Badge variant="neutral" size="sm">+{category.topics.length - 3} more</Badge>
                    )}
                  </div>
                </div>

                <div className="pt-4 border-t border-neutral-200">
                  <div className="text-xs text-neutral-500">
                    <div className="font-semibold mb-1">Recommended For:</div>
                    {category.recommendedFor}
                  </div>
                  <div className="mt-2 text-xs font-semibold text-neutral-700">
                    {sortedTemplates.length} homework types available
                  </div>
                </div>

                {isSelected && (
                  <div className="mt-4">
                    <Button variant="primary" fullWidth size="sm">
                      Browse Worksheets
                    </Button>
                  </div>
                )}
              </Card>
            );
          })}
        </div>

        {/* Worksheet Selection */}
        {selectedCategory && (
          <Card>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-neutral-900">
                  {HOMEWORK_RESOURCES[selectedCategory].title} Worksheets
                </h2>
                <p className="text-neutral-600 mt-1">
                  Browse by homework type, then open a specific worksheet.
                </p>
              </div>
              <Button variant="ghost" onClick={() => setSelectedCategory(null)}>
                Close
              </Button>
            </div>

            {downloadError && (
              <div className="mb-4 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                {downloadError}
              </div>
            )}

            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {sortedTemplates.map((template) => (
                <div
                  key={template.id}
                  className="p-4 border border-neutral-200 rounded-lg hover:border-primary-300 hover:bg-primary-50/30 transition-all cursor-pointer"
                  onClick={() => handleDownload(template)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="font-semibold text-neutral-900 mb-1">
                        {template.type || `${template.setId} - ${template.title}`}
                      </div>
                      <div className="text-sm text-neutral-600">
                        {template.description}
                      </div>
                      <div className="mt-2 flex items-center gap-2">
                        <Badge variant="neutral" size="sm">
                          {template.estimatedTime} min
                        </Badge>
                        {template.therapyPhase && (
                          <Badge variant="secondary" size="sm">
                            {template.therapyPhase}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="flex-shrink-0 ml-2">
                      {downloadingIds.has(template.id) ? (
                        <svg className="w-5 h-5 text-neutral-400 animate-spin" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                      ) : (
                        <Download className="w-5 h-5 text-neutral-400" />
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Help Section */}
        <Card className="mt-8 bg-gradient-to-r from-neutral-50 to-neutral-100">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 p-3 bg-neutral-700 rounded-lg text-white">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-bold text-neutral-900 mb-2">
                How to Use These Resources
              </h3>
              <ul className="text-neutral-600 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-primary-500 font-bold">•</span>
                  <span>These worksheets are designed to complement your daily Mind/Move/Journal practice</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary-500 font-bold">•</span>
                  <span>Work through them at your own pace, or follow your clinician's recommendations</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary-500 font-bold">•</span>
                  <span>Each worksheet is evidence-based and designed by licensed clinical professionals</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary-500 font-bold">•</span>
                  <span>Download PDFs to your device and complete them offline if preferred</span>
                </li>
              </ul>
            </div>
          </div>
        </Card>
      </main>

      <SOSButton variant="floating" />
    </>
  );
};

export default HomeworkResourcesPage;
